import { Box, Container, makeStyles, Tab, Tabs } from '@material-ui/core'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BuildingEnergyModelFragment,
  EnergySystemFragment,
  EnergySystemType,
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../generated/graphql'
import { CalibrationInputDhwTab } from './input-tabs/CalibrationInputDhwTab'
import { CalibrationInputSpaceHeatingTab } from './input-tabs/CalibrationInputSpaceHeatingTab'
import { convertInputParametersToFormParameters } from './input-tabs/input-util'
import { theme } from '../../../theme'
import { CalibrationInputHeatLoadTab } from './input-tabs/CalibrationInputHeatLoadTab'
import { CalibrationInputCoolingTab } from './input-tabs/CalibrationInputCoolingTab'
import { CalibrationInputElectricityTab } from './input-tabs/CalibrationInputElectricityTab'
import { CalibrationInputVentilationTab } from './input-tabs/CalibrationInputVentilationTab'
import { getDefaultVentilationFormValues } from './input-tabs/ventilation-form-values'

const useStyles = makeStyles((themeSpec) => ({
  header: {
    marginTop: themeSpec.spacing(2),
    marginBottom: themeSpec.spacing(2),
  },
}))

const getEnergySystem = (
  model: BuildingEnergyModelFragment,
  esType: EnergySystemType,
): EnergySystemFragment | undefined =>
  model.energySystems.find((system) => system.type === esType)

const getEnergySystemSchedule = (
  model: BuildingEnergyModelFragment,
  esType: EnergySystemType,
): ScheduleFragment | undefined =>
  model.schedules.find(
    (s) => s.id === getEnergySystem(model, esType)?.schedule?.id,
  )

type TabProps = {
  id: string
  order: number
  title: string
  type: EnergySystemType
  disabled: boolean
  component: JSX.Element
}

type Props = {
  model: BuildingEnergyModelFragment
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputsLayout({
  model,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const [selectedTab, setSelectedTab] = useState(0)

  const classes = useStyles()
  const { t } = useTranslation()

  const tabs: TabProps[] = useMemo(
    () => [
      {
        id: 'calibration-input-dhw-tab',
        order: 1,
        title: t('energySystemType.DomesticHotWater'),
        type: EnergySystemType.DomesticHotWater,
        disabled:
          getEnergySystem(model, EnergySystemType.DomesticHotWater) ===
          undefined,
        component: (
          <CalibrationInputDhwTab
            id={
              getEnergySystem(model, EnergySystemType.DomesticHotWater)?.id ??
              ''
            }
            defaultValues={convertInputParametersToFormParameters(
              getEnergySystem(model, EnergySystemType.DomesticHotWater),
              getEnergySystemSchedule(model, EnergySystemType.DomesticHotWater),
              model.area,
            )}
            schedules={model.schedules}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        ),
      },
      {
        id: 'calibration-input-space-heating-tab',
        order: 2,
        title: t('energySystemType.SpaceHeating'),
        type: EnergySystemType.SpaceHeating,
        disabled:
          getEnergySystem(model, EnergySystemType.SpaceHeating) === undefined,
        component: (
          <CalibrationInputSpaceHeatingTab
            id={getEnergySystem(model, EnergySystemType.SpaceHeating)?.id ?? ''}
            defaultValues={convertInputParametersToFormParameters(
              getEnergySystem(model, EnergySystemType.SpaceHeating),
              undefined,
              model.area,
            )}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        ),
      },
      {
        id: 'calibration-input-heatload-tab',
        order: 3,
        title: t('energySystemType.HeatLoad'),
        type: EnergySystemType.HeatLoad,
        disabled:
          getEnergySystem(model, EnergySystemType.HeatLoad) === undefined,
        component: (
          <CalibrationInputHeatLoadTab
            id={getEnergySystem(model, EnergySystemType.HeatLoad)?.id ?? ''}
            defaultValues={convertInputParametersToFormParameters(
              getEnergySystem(model, EnergySystemType.HeatLoad),
              undefined,
              model.area,
            )}
            schedules={model.schedules}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        ),
      },
      {
        id: 'calibration-input-ventilation-tab',
        order: 4,
        title: t('energySystemType.Ventilation'),
        type: EnergySystemType.Ventilation,
        disabled:
          getEnergySystem(model, EnergySystemType.Ventilation) === undefined,
        component: (
          <CalibrationInputVentilationTab
            id={getEnergySystem(model, EnergySystemType.Ventilation)?.id ?? ''}
            defaultValues={getDefaultVentilationFormValues(model)}
            schedules={model.schedules}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        ),
      },
      {
        id: 'calibration-input-cooling-tab',
        order: 5,
        title: t('energySystemType.Cooling'),
        type: EnergySystemType.Cooling,
        disabled:
          getEnergySystem(model, EnergySystemType.Cooling) === undefined,
        component: (
          <CalibrationInputCoolingTab
            id={getEnergySystem(model, EnergySystemType.Cooling)?.id ?? ''}
            defaultValues={convertInputParametersToFormParameters(
              getEnergySystem(model, EnergySystemType.Cooling),
              undefined,
              model.area,
            )}
            schedules={model.schedules}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        ),
      },
      {
        id: 'calibration-input-electricity-tab',
        order: 6,
        title: t('energySystemType.ElectricityConsumption'),
        type: EnergySystemType.ElectricityConsumption,
        disabled:
          getEnergySystem(model, EnergySystemType.ElectricityConsumption) ===
          undefined,
        component: (
          <CalibrationInputElectricityTab
            id={
              getEnergySystem(model, EnergySystemType.ElectricityConsumption)
                ?.id ?? ''
            }
            defaultValues={convertInputParametersToFormParameters(
              getEnergySystem(model, EnergySystemType.ElectricityConsumption),
              getEnergySystemSchedule(
                model,
                EnergySystemType.ElectricityConsumption,
              ),
              model.area,
            )}
            schedules={model.schedules}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        ),
      },
    ],
    [model], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <Box>
      <Container maxWidth={false}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className={classes.header}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, idx: number) => setSelectedTab(idx)}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                id={tab.id}
                label={tab.title}
                disabled={tab.disabled}
              />
            ))}
          </Tabs>
        </Box>
      </Container>
      <Container style={{ marginTop: theme.spacing(2) }} maxWidth={false}>
        {tabs[selectedTab].component}
      </Container>
    </Box>
  )
}
